import { z } from "zod";

import DataTypes from "~data";

import { Charge } from "../models/Charge";
import { Invoice } from "../models/Invoice";
import { CountryCode } from "../models/partials/CountryCode";
import { PaymentSourceData } from "../models/partials/PaymentSourceData";
import { UsStateCode } from "../models/partials/UsStateCode";
import { TeamBillingInfoData } from "../models/Team";
import { Invite } from "../models/TeamMembership";
import { ErrorResponse, SuccessResponse } from "./standard-responses";

export const settingsEndpoints = {
  "up.members.invite": (p: { teamDomain: string }) => ["up", "post", `/settings/teams/${p.teamDomain}/members/invites`],
  "up.members.invite.resend": (p: { teamDomain: string; inviteId: string }) => [
    "up",
    "post",
    `/settings/teams/${p.teamDomain}/members/invites/${p.inviteId}/resend`,
  ],
  "up.members.permissions.update": (p: { teamDomain: string; memberId: string }) => [
    "up",
    "put",
    `/settings/teams/${p.teamDomain}/members/${p.memberId}`,
  ],
  "up.members.invite.permissions.update": (p: { teamDomain: string; inviteId: string }) => [
    "up",
    "put",
    `/settings/teams/${p.teamDomain}/members/invites/${p.inviteId}`,
  ],
  "up.members.transferOwnership": (p: { teamDomain: string; memberId: string }) => [
    "up",
    "post",
    `/settings/teams/${p.teamDomain}/members/${p.memberId}/transfer`,
  ],
  "up.billing.history.details": (p: { teamDomain: string }) => [
    "up",
    "get",
    `/settings/teams/${p.teamDomain}/billing/history/details`,
  ],
  "up.billing.data": (p: { teamDomain: string }) => ["up", "get", `/settings/teams/${p.teamDomain}/billing/data`],
  "up.billing.link": (p: { teamDomain: string }) => ["up", "post", `/settings/teams/${p.teamDomain}/billing/link`],
  "up.billing.unlink": (p: { teamDomain: string }) => ["up", "delete", `/settings/teams/${p.teamDomain}/billing/link`],
  "up.billing.card.intent": (p: { teamDomain: string }) => [
    "up",
    "get",
    `/settings/teams/${p.teamDomain}/billing/card/token`,
  ],
  "up.billing.card.update": (p: { teamDomain: string; stripeToken: string }) => [
    "up",
    "post",
    `/settings/teams/${p.teamDomain}/billing/card`,
  ],
  "up.billing.licenses.update": (p: { teamDomain: string; activeLicenses: number; autoAdjustLicenses: string }) => [
    "up",
    "put",
    `/settings/teams/${p.teamDomain}/billing/licenses`,
  ],
  "up.billing.subscription.cancel": (p: { teamDomain: string }) => [
    "up",
    "delete",
    `/settings/teams/${p.teamDomain}/billing/subscription`,
  ],
  "up.billing.subscription.resume": (p: { teamDomain: string }) => [
    "up",
    "put",
    `/settings/teams/${p.teamDomain}/billing/subscription/on`,
  ],
  "up.billing.update": (p: { teamDomain: string }) => ["up", "put", `/settings/teams/${p.teamDomain}/billing/details`],
  "up.billing.sessionCredit.buy": (p: { teamDomain: string }) => [
    "up",
    "post",
    `/settings/teams/${p.teamDomain}/billing/session-credit/buy`,
  ],
  "up.billing.sessionCredit.settings.update": (p: { teamDomain: string }) => [
    "up",
    "put",
    `/settings/teams/${p.teamDomain}/billing/session-credit/settings`,
  ],

  "up.settings.integrations.salesforce.setup": (p: { teamDomain: string; sandbox: boolean }) => [
    "up",
    "get",
    `/settings/teams/${p.teamDomain}/integrations/add/salesforce${p.sandbox ? "?sandbox=true" : ""}`,
  ],
  "up.invoice.failed": (p: { invoiceId: string }) => ["up", "get", `/invoice/${p.invoiceId}/failed/data`],
  "up.invoice.reportPaid": (p: { invoiceId: string }) => ["up", "post", `/invoice/${p.invoiceId}/failed/paid`],
} as const satisfies Record<string, (p: any) => ["up", "get" | "delete" | "post" | "put", `/${string}`]>;

export const settingsRequests = {
  "up.members.invite": z.object({
    teamDomain: z.string(),
    email: z.string(),
  }),
  "up.members.invite.resend": z.object({
    teamDomain: z.string(),
    inviteId: z.string(),
  }),
  "up.members.transferOwnership": z.object({
    teamDomain: z.string(),
    memberId: z.string(),
  }),
  "up.members.permissions.update": z.object({
    permissions: z.object({
      canAccessVisitorsList: z.boolean().optional(),
      canScreenshare: z.boolean().optional(),
      canAccessSettings: z.boolean().optional(),
      canManageUsers: z.boolean().optional(),
      canManageBilling: z.boolean().optional(),
      canAccessActivityLog: z.boolean().optional(),
      canUseConsole: z.boolean().optional(),
      canDeleteVisitorData: z.boolean().optional(),
      canViewReporting: z.boolean().optional(),
      canPurchase: z.boolean().optional(),
    }),
    teamDomain: z.string(),
    memberId: z.string(),
  }),
  "up.members.invite.permissions.update": z.object({
    permissions: z.object({
      canAccessVisitorsList: z.boolean().optional(),
      canScreenshare: z.boolean().optional(),
      canAccessSettings: z.boolean().optional(),
      canManageUsers: z.boolean().optional(),
      canManageBilling: z.boolean().optional(),
      canAccessActivityLog: z.boolean().optional(),
      canUseConsole: z.boolean().optional(),
      canDeleteVisitorData: z.boolean().optional(),
      canViewReporting: z.boolean().optional(),
      canPurchase: z.boolean().optional(),
    }),
    teamDomain: z.string(),
    inviteId: z.string(),
  }),
  "up.billing.history.details": z.object({
    teamDomain: z.string(),
    page: z.number().optional().nullable(),
  }),
  "up.billing.data": z.object({
    teamDomain: z.string(),
  }),
  "up.billing.link": z.object({
    teamDomain: z.string(),
    subaccountId: z.string(),
  }),
  "up.billing.unlink": z.object({
    teamDomain: z.string(),
    subaccountId: z.string(),
  }),
  "up.billing.card.intent": z.object({
    teamDomain: z.string(),
  }),
  "up.billing.card.update": z.object({
    teamDomain: z.string(),
    intentTokenId: z.string(),
  }),
  "up.billing.licenses.update": z.object({
    teamDomain: z.string(),
    activeLicenses: z.number(),
    autoAdjustLicenses: z
      .custom<Extract<DataTypes["Teams"]["autoAdjustLicenses"], "off" | "increase" | "increase_and_decrease">>()
      .optional(),
  }),
  "up.billing.subscription.cancel": z.object({
    teamDomain: z.string(),
    cancelReason: z.string(),
    cancelDetails: z.string().optional(),
  }),
  "up.billing.subscription.resume": z.object({
    teamDomain: z.string(),
  }),
  "up.billing.update": z.object({
    teamDomain: z.string(),
    vatNumber: z.string().optional().nullable(),
    invoiceName: z.string().optional().nullable(),
    invoiceAddress: z.string().optional().nullable(),
    invoiceCountry: CountryCode.nullable(),
    invoiceZip: z.string().optional().nullable(),
    invoiceState: UsStateCode.optional().nullable(),
    invoiceEmail: z.string().optional().nullable(),
  }),
  "up.billing.sessionCredit.buy": z.object({
    teamDomain: z.string(),
    hours: z.number(),
  }),
  "up.billing.sessionCredit.settings.update": z.object({
    teamDomain: z.string(),
    team: TeamBillingInfoData.pick({
      settingSessionTimeCreditAutoTopUpEnabled: true,
      settingSessionTimeCreditAutoTopUpTrigger: true,
      settingSessionTimeCreditAutoTopUpMultiplier: true,
    }),
  }),

  "up.settings.integrations.salesforce.setup": z.object({
    teamDomain: z.string(),
    sandbox: z.boolean().optional(),
  }),
  "up.invoice.failed": z.object({
    invoiceId: z.string(),
  }),
  "up.invoice.reportPaid": z.object({
    invoiceId: z.string(),
    updateCardTokenId: z.string().optional(),
  }),
} as const;

export const settingsResponses = {
  "up.members.permissions.update": SuccessResponse,
  "up.members.invite.permissions.update": SuccessResponse,
  "up.members.invite.resend": SuccessResponse.extend({
    invite: Invite,
    removeId: z.string(),
  }),
  "up.members.transferOwnership": SuccessResponse,
  "up.members.invite": SuccessResponse.extend({
    invite: Invite,
  }).or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      inviteErrors: z.object({
        email: z.string(),
      }),
    }),
  ),
  "up.billing.history.details": SuccessResponse.extend({
    teamInvoices: Invoice.array(),
    teamCharges: Charge.omit({ amountRefundable: true }).array(),
  }),
  "up.billing.data": SuccessResponse.extend({
    team: TeamBillingInfoData,
    subaccounts: TeamBillingInfoData.array(),
    possibleBillingOwners: TeamBillingInfoData.array(),
  }),
  "up.billing.link": SuccessResponse.or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      teamErrors: z.object({
        billingOwner: z.string(),
      }),
    }),
  ),
  "up.billing.unlink": SuccessResponse,
  "up.billing.card.intent": SuccessResponse.extend({
    intentToken: z.object({
      id: z.string(),
      clientSecret: z.string(),
      publicKey: z.string(),
    }),
  }),
  "up.billing.card.update": SuccessResponse.extend({
    source: PaymentSourceData,
  }),
  "up.billing.licenses.update": SuccessResponse.extend({
    team: TeamBillingInfoData,
  }),
  "up.billing.subscription.cancel": SuccessResponse.extend({
    team: TeamBillingInfoData,
  }),
  "up.billing.subscription.resume": SuccessResponse.extend({
    team: TeamBillingInfoData,
  }),
  "up.billing.update": SuccessResponse.extend({
    invoiceDetails: z.object({
      invoiceAddress: z.string().nullable(),
      invoiceCountry: z.string().nullable(),
      invoiceEmail: z.string().nullable(),
      invoiceName: z.string().nullable(),
      invoiceZip: z.string().nullable(),
      invoiceState: z.string().nullable(),
      vatNumber: z.string().nullable(),
    }),
  }).or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      teamErrors: z.record(settingsRequests["up.billing.update"].keyof(), z.string()),
    }),
  ),
  "up.billing.sessionCredit.buy": SuccessResponse.extend({
    team: TeamBillingInfoData,
  }),
  "up.billing.sessionCredit.settings.update": SuccessResponse.extend({
    team: TeamBillingInfoData,
  }),

  "up.settings.integrations.salesforce.setup": SuccessResponse,
  "up.invoice.failed": SuccessResponse.extend({
    existingPaymentSource: PaymentSourceData.nullable(),
    invoice: Invoice,
    intentToken: z.object({
      id: z.string(),
      clientSecret: z.string(),
      publicKey: z.string(),
    }),
  })
    .or(ErrorResponse.extend({ error: z.literal("invoice_not_found") }))
    .or(ErrorResponse.extend({ error: z.literal("invoice_paid") })),
  "up.invoice.reportPaid": SuccessResponse,
} as const;
